// EJE FRONTEND (ARQUITECTURA_FRONT_PROPUESTA.md §7): hooks TanStack Query del caso de uso "ventas"
// del BO retail. Componen el service-client `api/services/retail-wizard/` (dueño = Andrés) — aquí
// no se arma ninguna URL ni se toca `fetch`. Query keys namespaced (`bo-sales*`) para que la
// invalidación de la cancelación no arrastre las queries del wizard del vendedor.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { backofficeSalesApi } from '@/api/services/retail-wizard/retail-wizard.client';
import type {
    BackofficeSalesFilters,
    BackofficeSalesPage,
    CentralResendKind,
} from '@/api/services/retail-wizard/contract';
import { homeClient } from '@/api/services/retail-wizard/backoffice/home.client';
import { customersBackofficeApi } from '@/api/services/retail-customer/backoffice';
import { STALE_SALE_CANCEL_CODES, type CancelSaleInput } from '@/shared/patterns/CancelSale';
import { serverSideFilters } from '../lib/ventasFilters';
import { ApiError } from '@/api/errors';

/**
 * El fin de la lista lo marca `nextToken`, NUNCA la cantidad de ítems recibidos: DynamoDB aplica el
 * FilterExpression después de leer la página, así que una respuesta corta no significa que se acabó.
 */
export function hasMorePages(page: BackofficeSalesPage): boolean {
    // `!= null` (y no `!==`) a propósito: equivale a "ni null ni undefined" en un solo chequeo, así
    // que también cubre un wire que omita el campo en la última página.
    return page.nextToken != null;
}

/**
 * La lista paginada. El término de búsqueda NO entra ni en la query key ni en el request: se filtra
 * en cliente sobre lo cargado (ver `serverSideFilters`). Si entrara, teclear una letra invalidaría
 * la query y tiraría las páginas que el operador ya había traído — justo lo contrario de buscar.
 */
export function useVentas(filters: BackofficeSalesFilters) {
    const paraElApi = serverSideFilters(filters);
    return useInfiniteQuery({
        queryKey: ['bo-sales', paraElApi],
        enabled: !!paraElApi.retailerId,
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam }) => backofficeSalesApi.list(paraElApi, pageParam),
        getNextPageParam: (last) =>
            hasMorePages(last) ? (last.nextToken ?? undefined) : undefined,
    });
}

export function useVenta(saleId: string | null) {
    return useQuery({
        queryKey: ['bo-sale', saleId],
        enabled: !!saleId,
        queryFn: () => backofficeSalesApi.get(saleId as string),
    });
}

export function useCancelVenta() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            saleId,
            reason,
            equipmentVerified,
            returnConditions = [],
            outOfWindowExceptionId,
        }: CancelSaleInput) =>
            backofficeSalesApi.cancel(
                saleId,
                reason,
                equipmentVerified,
                returnConditions,
                outOfWindowExceptionId,
            ),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: ['bo-sales'] });
            void qc.invalidateQueries({ queryKey: ['bo-sale', vars.saleId] });
        },
        // Un rechazo por el estado de la venta relee el detalle para que el botón se deshabilite.
        onError: (error, vars) => {
            if (error instanceof ApiError && STALE_SALE_CANCEL_CODES.has(error.code)) {
                void qc.invalidateQueries({ queryKey: ['bo-sale', vars.saleId] });
            }
        },
    });
}

/** El aviso a La Central ya no se puede reenviar: hay que releer la venta. */
export const CENTRAL_SYNC_NOT_RESENDABLE = 'CENTRAL_SYNC_NOT_RESENDABLE';

/** Reenvía un aviso REJECTED o FAILED a La Central y relee la lista y la venta. */
export function useResendCentral() {
    const qc = useQueryClient();
    const refresh = (saleId: string) => {
        void qc.invalidateQueries({ queryKey: ['bo-sales'] });
        void qc.invalidateQueries({ queryKey: ['bo-sale', saleId] });
    };
    return useMutation({
        mutationFn: ({ saleId, kind }: { saleId: string; kind: CentralResendKind }) =>
            backofficeSalesApi.resendCentral(saleId, kind),
        onSuccess: (_data, vars) => {
            refresh(vars.saleId);
        },
        onError: (error, vars) => {
            if (error instanceof ApiError && error.code === CENTRAL_SYNC_NOT_RESENDABLE) {
                refresh(vars.saleId);
            }
        },
    });
}

/**
 * Los contadores del retailer en la ventana — lo que HAY, no lo que se cargó.
 *
 * Va en su propio query y no dentro de la lista: son dos preguntas distintas y la barra de pills
 * tiene que poder seguir mostrando el total aunque la página de ventas esté recargando.
 *
 * `retry: false`: si el silo no contesta, la barra cae a contar lo cargado, que es un número
 * honesto — reintentar tres veces solo retrasa ese fallback.
 *
 * Solo viajan `from`/`to`: el endpoint ignora tienda, vendedor y tipo. Ver `serverStatsCoverFilters`.
 */
export function useVentasStats(filters: BackofficeSalesFilters) {
    return useQuery({
        queryKey: ['bo-sales-stats', filters.from, filters.to],
        enabled: !!filters.retailerId,
        queryFn: () => backofficeSalesApi.stats(filters.from, filters.to),
        staleTime: 60_000,
        retry: false,
    });
}

/**
 * La bitácora auditada de la venta — el tab «Bitácora» del drawer.
 *
 * Hasta hoy ese tab se dibujaba con los campos de la propia venta (cierre y cancelación) porque no
 * había otra fuente. Esta ES la otra fuente: el audit trail, con su diff campo a campo.
 */
export function useVentaLog(saleId: string | null) {
    return useQuery({
        queryKey: ['bo-sale-log', saleId],
        enabled: !!saleId,
        queryFn: () => backofficeSalesApi.log(saleId as string),
        staleTime: 30_000,
        retry: false,
    });
}

/** Vendido hoy, piezas de hoy, su base de hace 7 días y la meta del día de la cadena. */
export function useVentasDayKpis(enabled: boolean) {
    return useQuery({
        queryKey: ['bo-sales-kpis'],
        enabled,
        queryFn: () => homeClient.getSalesKpis(),
        staleTime: 60_000,
        retry: false,
    });
}

/** Ficha del cliente de la venta, de donde sale su teléfono. */
export function useVentaCustomer(retailCustomerId: string | null, retailerId: string | null) {
    return useQuery({
        queryKey: ['bo-sale-customer', retailCustomerId, retailerId],
        enabled: !!retailCustomerId,
        queryFn: () =>
            customersBackofficeApi.getCustomer(retailCustomerId ?? '', retailerId ?? undefined),
        staleTime: 60_000,
        retry: false,
    });
}
